import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { EnvelopeIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { authService } from '../../services';
import { Button, Input, Card, CardBody } from '../../components/ui';

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');

  const { register, handleSubmit, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await authService.forgotPassword(data.email);
      setEmail(data.email);
      setSent(true);
      toast.success(t('auth.resetEmailSent'));
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('auth.forgotPassword')} - ProCTM</title>
      </Helmet>

      <Card className="w-full max-w-md">
        <CardBody className="p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-neutral-dark">{t('auth.forgotPassword')}</h1>
            <p className="mt-2 text-gray-500">
              {sent ? t('auth.resetEmailSent') : t('auth.enterEmailToReset')}
            </p>
          </div>

          {!sent ? (
            <>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <Input
                  label={t('auth.email')}
                  type="email"
                  icon={EnvelopeIcon}
                  error={errors.email?.message}
                  placeholder={t('auth.emailPlaceholder') || 'you@example.com'}
                  {...register('email', {
                    required: t('errors.required'),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('errors.invalidEmail')
                    }
                  })}
                />

                <Button type="submit" className="w-full" loading={loading}>
                  {t('auth.sendResetLink')}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <ArrowLeftIcon className="w-4 h-4 rtl:rotate-180" />
                  {t('auth.backToLogin')}
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-success/10 rounded-full flex items-center justify-center">
                <EnvelopeIcon className="w-8 h-8 text-success" />
              </div>
              <h2 className="text-lg font-semibold text-neutral-dark mb-2">
                {t('auth.checkYourEmail')}
              </h2>
              <p className="text-gray-500 mb-6">
                {t('auth.resetLinkSentTo')} <span className="font-medium text-neutral-dark">{email}</span>
              </p>
              <p className="text-sm text-gray-500 mb-6">
                {t('auth.resetLinkInstructions')}
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full">
                  {t('auth.backToLogin')}
                </Button>
              </Link>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}
